import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api, client } from '@/api/client';
import { enableMobilePush as registerMobilePush } from '@/mobile-push';
import { useUiStore } from './ui.store';
import type { NotificationItem, Page } from '@/types';

/** Notification inbox, kept live by the realtime channel. */
export const useNotificationsStore = defineStore('notifications', () => {
  const ui = useUiStore();

  const items = ref<NotificationItem[]>([]);
  const unread = ref(0);
  /** Cards due or overdue for the current user; drives the inbox badge. */
  const inboxUrgent = ref(0);
  const loading = ref(false);
  const channels = ref<string[]>([]);
  const webPushPublicKey = ref<string | null>(null);
  let subscribed = false;

  async function load(page = 1, unreadOnly = false): Promise<void> {
    loading.value = true;
    try {
      const response = await api.get<Page<NotificationItem>>('notifications', { page, limit: 30, unread: unreadOnly ? 'true' : undefined });
      items.value = page === 1 ? response.items : [...items.value, ...response.items];
    } finally {
      loading.value = false;
    }
  }

  async function refreshUnread(): Promise<void> {
    const response = await api.get<{ count: number }>('notifications/unread-count');
    unread.value = response.count;
  }

  /**
   * Work waiting on the user — cards due or overdue — for the inbox badge.
   * Counted server-side rather than derived from a loaded inbox, so the badge
   * costs a `COUNT(*)` and not two hundred hydrated cards.
   */
  async function refreshInbox(companyId?: string | null): Promise<void> {
    const response = await api.get<{ urgent: number; unread: number; total: number }>('inbox/count', { companyId: companyId ?? undefined });
    inboxUrgent.value = response.urgent;
  }

  async function loadChannels(): Promise<void> {
    const response = await api.get<{ channels: string[]; webPushPublicKey: string | null }>('notifications/channels');
    channels.value = response.channels;
    webPushPublicKey.value = response.webPushPublicKey;
  }

  async function markRead(ids: string[]): Promise<void> {
    await api.post('notifications/read', { ids });
    for (const item of items.value) {
      if (ids.includes(item.id) && !item.readAt) item.readAt = new Date().toISOString();
    }
    unread.value = Math.max(0, unread.value - ids.length);
  }

  async function markAllRead(): Promise<void> {
    await api.post('notifications/read-all');
    const now = new Date().toISOString();
    items.value = items.value.map((item) => ({ ...item, readAt: item.readAt ?? now }));
    unread.value = 0;
  }

  async function remove(id: string): Promise<void> {
    await api.delete(`notifications/${id}`);
    items.value = items.value.filter((item) => item.id !== id);
  }

  /** Subscribes to live notifications; called once after sign-in. */
  function listen(): void {
    if (subscribed) return;
    subscribed = true;

    client.realtime.on<NotificationItem & { title: string; body: string }>('notification', (payload) => {
      items.value = [{ ...payload, readAt: null, data: payload.data ?? {} } as NotificationItem, ...items.value];
      unread.value += 1;
      ui.info(payload.title, payload.body);

      // Mirror to the OS when the user granted permission.
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(payload.title, { body: payload.body, tag: payload.id });
      }
    });
  }

  /** Registers a Web Push subscription for this browser. */
  async function enableWebPush(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !webPushPublicKey.value) return false;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const registration = await navigator.serviceWorker.register('/sw.js').catch(() => null);
    if (!registration) return false;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(webPushPublicKey.value) as BufferSource,
    });

    const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    await api.post('users/me/push-subscriptions', {
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    });
    return true;
  }

  /** Requests the native permission and registers this installation's FCM token. */
  function enableMobilePush(): Promise<boolean> {
    return registerMobilePush();
  }

  return {
    items,
    unread,
    inboxUrgent,
    loading,
    channels,
    webPushPublicKey,
    load,
    refreshUnread,
    refreshInbox,
    loadChannels,
    markRead,
    markAllRead,
    remove,
    listen,
    enableWebPush,
    enableMobilePush,
  };
});

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}
